<template>
  <div
    ref="wrapper"
    class="directions scrollbar-none max-md:overflow-scroll"
    :class="[
      {
        'max-md:-ml-4': isScrolled,
      },
      'max-md:-mr-4',
      ui?.root,
    ]"
    @scroll="handleScroll"
  >
    <slot name="all-button">
      <UButton
        class="mb-6 ml-auto block max-xl:hidden"
        color="neutral"
        variant="soft"
        trailing-icon="i-local-move-up-right"
        to="/directions"
        :ui="{
          trailingIcon: 'w-4 h-4 align-middle',
        }"
      >
        Все направления
      </UButton>
    </slot>

    <ClientOnly>
      <div class="directions-list-wrapper">
        <template v-for="(row, rIndex) in preparedDirections" :key="rIndex">
          <div class="directions-list" :class="ui?.list">
            <SPDirection
              v-for="(direction, index) in row"
              :key="index"
              :direction="direction"
            />

            <ClientOnly>
              <ULink
                v-if="
                  isLessThan('xl') && rIndex === preparedDirections.length - 1
                "
                class="flex relative justify-center items-center p-3 h-25 w-37.75 max-lg:w-42 max-md:w-37.5 max-md:shrink-0 bg-white rounded-[16px]"
                to="/directions"
              >
                <span
                  class="text-accent-default! text-size-h6 text-center font-semibold max-w-3/4 max-lg:max-w-full"
                >
                  <slot name="button-label">Все направления</slot>
                  <slot name="button">
                    <IconBadge
                      class="pointer-events-none absolute top-3 right-3 h-6 w-6 justify-center bg-accent-default text-white"
                      icon="i-local-move-up-right"
                    />
                  </slot>
                </span>
              </ULink>
            </ClientOnly>
          </div>
        </template>
      </div>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import type { Direction } from '~/api'
import { chunkArray } from '~/shared'
import SPDirection from './main-page/SPDirection.vue'
import IconBadge from './IconBadge.vue'

defineOptions({
  name: 'SPDirections',
})

defineProps<{
  ui?: {
    root?: string
    list?: string
    direction?: string
    directionName?: string
    directionIllustration?: string
  }
}>()

defineSlots<{
  'all-button': () => void
  button: () => any
  'button-label': () => any
}>()

const breakpoints = useBreakpointsStore()
const { isBigTablet, isSmallTablet } = storeToRefs(breakpoints)
const { isLessThan, isMoreThan } = breakpoints

const directoryStore = useDirectoryStore()
const { directions } = storeToRefs(directoryStore)

const wrapper = useTemplateRef('wrapper')
const isScrolled = ref(false)

const preparedDirections = computed<Partial<Direction>[][]>(() => {
  const $directions = directions?.value?.filter(
    (dir) => dir.name !== 'Спортивное плавание',
  )

  if (isBigTablet.value) {
    return chunkArray(
      $directions.filter((_, index) => index < 11),
      6,
    )
  }

  if (isSmallTablet.value) {
    return chunkArray(
      $directions.filter((_, index) => index < 11),
      4,
    )
  }

  if (isMoreThan('md')) {
    return [$directions?.filter((_, index) => index < 7)]
  }

  if (isLessThan('md')) {
    return chunkArray(
      $directions.filter((_, index) => index < 11),
      6,
    )
  }

  return [$directions]
})

const handleScroll = (event: Event) => {
  if (import.meta.client) {
    isScrolled.value = !!(event.target as HTMLElement)?.scrollLeft
  }
}
</script>

<style lang="scss" scoped>
@use 'sass:list';

.directions-list {
  --directions-gap: 12px;
  --columns: 7;
  display: flex;
  flex: 0 1;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  gap: var(--directions-gap);
  overflow-x: auto;

  &:not(:last-of-type) {
    margin-bottom: var(--directions-gap);
  }

  @include isLessThan('xl') {
    --directions-gap: 6px;
    --columns: 6;
  }

  @include isSmallTablet {
    --columns: 4;
  }

  @include max-md {
    flex-wrap: nowrap;
    overflow: visible;

    $row-widths: (
      (140px, 110px, 150px, 110px, 150px, 110px),
      (110px, 140px, 110px, 150px, 110px, 150px)
    );

    @for $row-idx from 1 through list.length($row-widths) {
      $current-row: list.nth($row-widths, $row-idx);

      &:nth-child(#{$row-idx}) {
        @for $col-idx from 1 through list.length($current-row) {
          .direction:nth-child(#{$col-idx}) {
            width: list.nth($current-row, $col-idx);
          }
        }
      }
    }
  }
}
</style>
